import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Region, detectRegion, getDefaultConsent, getBannerConfig } from '../services/geolocationService';

// Consent state interface
export interface ConsentState {
  essential: boolean;     // Always true - required for site functionality
  analytics: boolean;     // Google Analytics, usage tracking
  marketing: boolean;     // Marketing cookies, retargeting
}

// Full consent context value
interface ConsentContextValue {
  consent: ConsentState;
  region: Region;
  consentGiven: boolean;      // User has made a choice
  showBanner: boolean;        // Should show consent banner
  bannerConfig: ReturnType<typeof getBannerConfig>;
  isLoading: boolean;         // Still detecting region
  // Actions
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (consent: Partial<ConsentState>) => void;
  openSettings: () => void;
  closeSettings: () => void;
  showSettingsModal: boolean;
  resetConsent: () => void;   // Re-show banner (for Cookie Settings button)
}

// Both are mirrored in the consent bootstrap in public/index.html — change them together.
const STORAGE_KEY = 'axoix_cookie_consent';
// Increment when consent requirements change. 1.1 (2026-10-02): Clarity added and
// the choice now actually gates GA4, Google Ads and the Meta Pixel — Cookie
// Policy §10 promises to ask again after a change like that.
const CONSENT_VERSION = '1.1';

// Cookie Policy §9: a Do Not Track or Global Privacy Control signal counts as
// declining optional cookies until the visitor chooses otherwise.
const privacySignalSet = (): boolean => {
  try {
    const nav = navigator as any;
    return nav.doNotTrack === '1' || (window as any).doNotTrack === '1' || nav.globalPrivacyControl === true;
  } catch {
    return false;
  }
};

/**
 * First-party attribution cookies (axoix_ref, axoix_attr, axoix_vertical):
 * EU/EEA/UK only with Marketing consent; everywhere else always, as Cookie
 * Policy §3.5 declares. One rule, used by ReferralTracker and verticalIntent.
 */
export const attributionAllowed = (region: Region, marketing: boolean): boolean =>
  region !== 'EU' || marketing;

// The same decision for code outside React (utils/verticalIntent.ts).
// null until the region has been resolved.
let attributionNow: boolean | null = null;
export const attributionAllowedNow = (): boolean | null => attributionNow;

const defaultsFor = (region: Region): ConsentState =>
  privacySignalSet()
    ? { essential: true, analytics: false, marketing: false }
    : getDefaultConsent(region);

/**
 * Push a consent state to the tags index.html loads on every page. index.html
 * sets the starting state (Google consent defaults, fbq revoke) before any tag
 * fires; this keeps them in step with every later decision. Clarity is gated
 * separately by ClarityLoader, which reads this same context.
 */
const applyConsentToTags = (c: ConsentState): void => {
  try {
    const w = window as any;
    if (typeof w.gtag === 'function') {
      const ads = c.marketing ? 'granted' : 'denied';
      w.gtag('consent', 'update', {
        analytics_storage: c.analytics ? 'granted' : 'denied',
        ad_storage: ads,
        ad_user_data: ads,
        ad_personalization: ads,
      });
    }
    if (typeof w.fbq === 'function') {
      w.fbq('consent', c.marketing ? 'grant' : 'revoke');
    }
  } catch {
    // never let consent plumbing break the app
  }
};

// Default context value
const defaultContextValue: ConsentContextValue = {
  consent: { essential: true, analytics: false, marketing: false },
  region: 'OTHER',
  consentGiven: false,
  showBanner: true,
  bannerConfig: getBannerConfig('OTHER'),
  isLoading: true,
  acceptAll: () => {},
  rejectNonEssential: () => {},
  updateConsent: () => {},
  openSettings: () => {},
  closeSettings: () => {},
  showSettingsModal: false,
  resetConsent: () => {},
};

const ConsentContext = createContext<ConsentContextValue>(defaultContextValue);

// Hook to use consent context
export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

interface StoredConsent {
  consent: ConsentState;
  region: Region;
  timestamp: number;
  version: string;
}

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentState>({ essential: true, analytics: false, marketing: false });
  const [region, setRegion] = useState<Region>('OTHER');
  const [consentGiven, setConsentGiven] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize: check stored consent or detect region
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check for stored consent
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored) {
          const parsed: StoredConsent = JSON.parse(stored);

          // Check if consent is still valid (same version, less than 1 year old)
          const isValid =
            parsed.version === CONSENT_VERSION &&
            Date.now() - parsed.timestamp < 365 * 24 * 60 * 60 * 1000;

          if (isValid) {
            setConsent(parsed.consent);
            setRegion(parsed.region);
            setConsentGiven(true);
            setShowBanner(false);
            setIsLoading(false);
            return;
          }
        }

        // No valid stored consent, detect region
        const detectedRegion = await detectRegion();
        setRegion(detectedRegion);

        // Set default consent based on region (and any DNT/GPC signal)
        const defaultConsent = defaultsFor(detectedRegion);
        setConsent(defaultConsent);

        // Show banner for new users
        setShowBanner(true);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize consent:', error);
        setIsLoading(false);
        setShowBanner(true);
      }
    };

    initialize();
  }, []);

  // Every resolved consent state — region default, stored choice, or a change
  // made in the banner — reaches the tags.
  useEffect(() => {
    if (!isLoading) applyConsentToTags(consent);
  }, [consent, isLoading]);

  useEffect(() => {
    attributionNow = isLoading ? null : attributionAllowed(region, consent.marketing);
  }, [isLoading, region, consent.marketing]);

  // Save consent to localStorage
  const saveConsent = useCallback((newConsent: ConsentState, newRegion: Region) => {
    const stored: StoredConsent = {
      consent: newConsent,
      region: newRegion,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent: ConsentState = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(newConsent);
    setConsentGiven(true);
    setShowBanner(false);
    setShowSettingsModal(false);
    saveConsent(newConsent, region);
  }, [region, saveConsent]);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(() => {
    const newConsent: ConsentState = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setConsent(newConsent);
    setConsentGiven(true);
    setShowBanner(false);
    setShowSettingsModal(false);
    saveConsent(newConsent, region);
  }, [region, saveConsent]);

  // Update specific consent preferences
  const updateConsent = useCallback((updates: Partial<ConsentState>) => {
    const newConsent: ConsentState = {
      ...consent,
      ...updates,
      essential: true, // Always keep essential
    };
    setConsent(newConsent);
    setConsentGiven(true);
    setShowBanner(false);
    setShowSettingsModal(false);
    saveConsent(newConsent, region);
  }, [consent, region, saveConsent]);

  // Open/close settings modal
  const openSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  // Reset consent (re-show banner)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsentGiven(false);
    setShowBanner(true);
    const defaultConsent = defaultsFor(region);
    setConsent(defaultConsent);
  }, [region]);

  const value: ConsentContextValue = {
    consent,
    region,
    consentGiven,
    showBanner,
    bannerConfig: getBannerConfig(region),
    isLoading,
    acceptAll,
    rejectNonEssential,
    updateConsent,
    openSettings,
    closeSettings,
    showSettingsModal,
    resetConsent,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export default ConsentContext;
